import {
  APPROXIMATE_HINT,
  IntervalQualifiers as Qualifiers,
  UNCERTAIN_HINT,
} from "@/common/qualifiers";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

interface QualifierChipProps {
  symbol: string;
  label: string;
  hint: string;
  active: boolean;
  onToggle(): void;
}

function QualifierChip({
  symbol,
  label,
  hint,
  active,
  onToggle,
}: QualifierChipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-pressed={active}
          onClick={onToggle}
          className={
            "flex min-h-8 shrink-0 touch-manipulation items-center gap-1 rounded-md border px-2 text-[11.5px] transition-colors " +
            (active
              ? "border-primary/60 bg-primary/10 text-foreground"
              : "border-border bg-card text-ink2 hover:bg-row-hover")
          }
        >
          <span
            className={
              "font-mono text-[12px] leading-none " +
              (active ? "text-primary" : "text-muted-foreground")
            }
          >
            {symbol}
          </span>
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[220px]">{hint}</TooltipContent>
    </Tooltip>
  );
}

interface QualifierTogglesProps {
  value: Qualifiers;
  onChange(value: Qualifiers): void;
  className?: string;
}

/**
 * The EDTF qualifier pair for one endpoint of a record's time. Both flags are
 * independent: neither set is an exact date, both set is EDTF's `%`.
 *
 * Which endpoint they belong to is the caller's to convey — inside the picker
 * header they edit whichever From/To chip is focused, and that chip shows the
 * qualified name back, so repeating the label here would only duplicate it.
 */
export function QualifierToggles({
  value,
  onChange,
  className,
}: QualifierTogglesProps) {
  return (
    <div className={"flex min-w-0 items-center gap-1.5 " + (className ?? "")}>
      <QualifierChip
        symbol="~"
        label="Approx."
        hint={APPROXIMATE_HINT}
        active={!!value.approximate}
        onToggle={() => onChange({ ...value, approximate: !value.approximate })}
      />
      <QualifierChip
        symbol="?"
        label="Uncertain"
        hint={UNCERTAIN_HINT}
        active={!!value.uncertain}
        onToggle={() => onChange({ ...value, uncertain: !value.uncertain })}
      />
    </div>
  );
}
